import { useEffect, useMemo, useState } from "react";
import { http, downloadUrl, formatApiError } from "../lib/api";
import { fmtTL, fmtNum, currentMonthRange } from "../lib/format";
import { FileDown, AlertTriangle, Receipt } from "lucide-react";
import { toast } from "../lib/notify";
import { useSearchParams, useNavigate } from "react-router-dom";
import { safePath } from "../lib/nav";
import SearchableSelect from "../components/SearchableSelect";
import PeriodPicker from "../components/claims/PeriodPicker";
import MultiSelectFilter from "../components/MultiSelectFilter";
import ClaimHistory from "../components/ClaimHistory";
import CorrectionsBlock from "../components/claims/CorrectionsBlock";
import ClaimGroupedTable from "../components/claims/ClaimGroupedTable";
import MissingSubClaims from "../components/claims/MissingSubClaims";
import { SummaryRow } from "../components/claims/SummaryRow";
import { useClaimInvoices } from "../components/claims/useClaimInvoices";

const ITEM_HEADERS = ["Hizmet", "Miktar", "Birim", "Birim Fiyat", "Toplam"];
// Hizmet adı sola, birim ortaya, sayısal sütunlar sağa
const itemAlign = (i) => {
  if (i === 2) return "text-center";
  return i === 0 ? "text-left" : "text-right";
};

export default function SubcontractorClaim() {
  const [projects, setProjects] = useState([]);
  const [subs, setSubs] = useState([]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const returnBack = safePath(searchParams.get("don"));   // ?don= ile gelindi (ör. maliyet analizi): Hakediş Oluştur sonrası ve ESC ile o adrese dönülür
  const [subcontractor_id, setSub] = useState(() => searchParams.get("sub") || "");
  const [project_id, setProject] = useState(() => searchParams.get("proje") || "");
  const [region_ids, setRegionIds] = useState(() => (searchParams.get("bolge") || "").split(",").filter(Boolean));
  const [category_id, setCategory] = useState(() => searchParams.get("ana") || "");
  const [service_id, setService] = useState(() => searchParams.get("hizmet") || "");
  const [date_from, setFrom] = useState(() => searchParams.get("bas") || currentMonthRange().from);
  const [date_to, setTo] = useState(() => searchParams.get("bit") || currentMonthRange().to);
  const [data, setData] = useState(null);
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [creating, setCreating] = useState(false);
  const [missingKey, setMissingKey] = useState(0);
  const [claimInvoices, reloadClaimInvoices] = useClaimInvoices(subcontractor_id ? { direction: "expense", subcontractor_id } : null);

  const createInvoice = async () => {
    if (!subcontractor_id) return;
    if (!project_id) return toast.error("Taşeron hakedişi için önce proje seçin");
    if (!category_id) return toast.error("Hakediş oluşturmak için tek bir Ana Başlık seçin");
    if (data && data.rows.length > 0 && !window.confirm(`${fmtTL(data.summary.total_subcontractor)} tutarında taşeron gider faturası oluşturulsun mu?`)) return;
    setCreating(true);
    try {
      const { data: inv } = await http.post("/reports/subcontractor-claim/create-invoice", {
        subcontractor_id,
        project_id,
        region_ids: region_ids.length ? region_ids : null,
        category_id: category_id || null,
        service_id: service_id || null,
        date_from: date_from || null,
        date_to: date_to || null,
      });
      toast.success(`${inv.invoice_no} numaralı gider faturası oluşturuldu — Faturalar sayfasında görüntüleyebilirsiniz.`);
      reloadClaimInvoices(); setMissingKey((k) => k + 1);
      if (returnBack) { toast.info("Maliyet analizine aynı filtrelerle dönülüyor"); navigate(returnBack); }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    Promise.all([http.get("/caris"), http.get("/projects")]).then(([c, p]) => {
      const projNames = new Set(p.data.map((x) => (x.name || "").trim().toLocaleLowerCase("tr")));
      setSubs(c.data.filter((s) => s.status !== "Pasif" && !projNames.has((s.name || "").trim().toLocaleLowerCase("tr"))));
      setProjects(p.data.filter((x) => x.status !== "Pasif"));
    });
    http.get("/regions").then((r) => setRegions(r.data));
    http.get("/categories").then((r) => setCategories(r.data));
    http.get("/services").then((r) => setServices(r.data));
  }, []);

  // projede dönem içinde iş yapmış taşeronlar (eksik hakediş listesi ile aynı kaynak) → taşeron seçeneklerini daraltır
  const [projectSubs, setProjectSubs] = useState(null);
  const [projectSubIds, setProjectSubIds] = useState(null);   // projede var olan taşeronlar (şube/atama/kayıt) — olmayan taşeron listelenmez
  useEffect(() => {
    if (!project_id) { setProjectSubs(null); setProjectSubIds(null); return; }
    http.get(`/projects/${project_id}/subcontractors`).then((r) => setProjectSubIds(new Set(r.data.ids))).catch(() => setProjectSubIds(null));
  }, [project_id]);
  useEffect(() => {
    if (!project_id) { return; }
    const params = { project_id };
    if (date_from) params.date_from = date_from;
    if (date_to) params.date_to = date_to;
    http.get("/reports/subcontractor-claim/missing", { params }).then((r) => setProjectSubs(r.data.rows)).catch(() => setProjectSubs(null));
  }, [project_id, date_from, date_to, missingKey]);

  useEffect(() => {
    if (!subcontractor_id) { setData(null); return; }
    const params = { subcontractor_id };
    if (project_id) params.project_id = project_id;
    if (region_ids.length) params.region_ids = region_ids.join(",");
    if (category_id) params.category_id = category_id;
    if (service_id) params.service_id = service_id;
    if (date_from) params.date_from = date_from;
    if (date_to) params.date_to = date_to;
    http.get("/reports/subcontractor-claim", { params }).then((r) => setData(r.data));
  }, [subcontractor_id, project_id, region_ids, category_id, service_id, date_from, date_to]);

  const byNameTr = (a, b) => (a.name || "").localeCompare(b.name || "", "tr");
  const subOptions = useMemo(() => {
    const st = {}; (projectSubs || []).forEach((r) => { st[r.subcontractor_id] = st[r.subcontractor_id] === "missing" ? "missing" : r.status; });
    // proje seçiliyse yalnız o projede var olan taşeronlar (dönemde çalışanlar + şube/atama kartında olanlar); seçili taşeron her durumda korunur
    const sorted = [...subs].sort(byNameTr).filter((s) => !project_id || !projectSubIds || projectSubIds.has(s.id) || st[s.id] || s.id === subcontractor_id);
    const tag = { missing: " — hakediş yok", draft: " — taslak", approved: " — oluşturuldu" };
    const inPeriod = sorted.filter((s) => st[s.id]).map((s) => ({ value: s.id, label: `${s.name}${tag[st[s.id]] || ""}` }));
    const others = sorted.filter((s) => !st[s.id]).map((s) => ({ value: s.id, label: project_id && projectSubs?.length ? `${s.name} (bu dönemde kayıt yok)` : s.name }));
    return [...inPeriod, ...others];
  }, [subs, projectSubs, projectSubIds, project_id, subcontractor_id]);
  const projectOptions = useMemo(() => [...projects].sort(byNameTr).map((p) => ({ value: p.id, label: p.name })), [projects]);
  const visibleCategories = useMemo(() => categories.filter((c) => c.scope !== "projects" || (c.project_ids || []).includes(project_id)), [categories, project_id]);
  const visibleServices = useMemo(() => services.filter((s) => s.category_id === category_id && (!s.project_id || s.project_id === project_id)).sort(byNameTr), [services, category_id, project_id]);
  const regionOptions = useMemo(() => regions.filter((r) => r.project_id === project_id).map((r) => ({ value: r.id, label: r.name })), [regions, project_id]);

  const qs = new URLSearchParams({
    subcontractor_id,
    ...(project_id && { project_id }),
    ...(region_ids.length && { region_ids: region_ids.join(",") }),
    ...(category_id && { category_id }),
    ...(service_id && { service_id }),
    ...(date_from && { date_from }),
    ...(date_to && { date_to }),
  }).toString();

  const returnTo = `/taseron-hakedis?sub=${subcontractor_id}&proje=${project_id}&bolge=${region_ids.join(",")}&ana=${category_id}&hizmet=${service_id}&bas=${date_from}&bit=${date_to}`;

  return (
    <div className="space-y-6" data-testid="page-subcontractor-claim">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Taşeron Hakedişi</h1>
          <p className="text-sm text-slate-500 mt-1">Önce <span className="font-semibold text-red-700">proje</span> seçin: dönemde iş yapan taşeronlar ve eksik hakedişler listelenir — tek tek ya da tümü için oluşturun. Ayrıntılı hakediş için taşeron seçin.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={createInvoice} disabled={!subcontractor_id || creating} data-testid="btn-create-sub-invoice"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 transition-colors">
            <Receipt className="w-4 h-4" /> {creating ? "Oluşturuluyor…" : "Hakediş Oluştur"}
          </button>
          <a
            href={downloadUrl(`/reports/subcontractor-claim/export?${qs}`)}
            target="_blank" rel="noreferrer"
            aria-disabled={!subcontractor_id}
            onClick={(e) => { if (!subcontractor_id) e.preventDefault(); }}
            className={`excel-btn ${!subcontractor_id ? "opacity-50 pointer-events-none" : ""}`}
            data-testid="btn-export-excel"
          >
            <FileDown className="w-4 h-4" /> Excel{"\u2019"}e Aktar
          </a>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <div className="space-y-1 max-w-2xl">
          <label className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">Proje *</label>
          <SearchableSelect testid="filter-project" value={project_id} onChange={(v) => { setProject(v); setSub(""); setRegionIds([]); setCategory(""); setService(""); }}
            placeholder="Proje arayın…"
            options={projectOptions} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <div className="space-y-1 lg:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Taşeron <span className="text-slate-400 normal-case tracking-normal">(ayrıntılı hakediş için)</span></label>
          <SearchableSelect testid="filter-subcontractor" value={subcontractor_id} disabled={!project_id} onChange={(v) => { setSub(v); setService(""); }}
            placeholder={project_id ? (projectSubIds && !subOptions.length ? "Bu projede taşeron yok" : "Taşeron arayın…") : "Önce proje seçin"}
            options={subOptions} />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Ana Başlık</label>
          <select data-testid="filter-category" disabled={!project_id} value={category_id} onChange={(e) => { setCategory(e.target.value); setService(""); }} className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-md disabled:bg-slate-50 disabled:text-slate-400">
            <option value="">Seçiniz…</option>
            {visibleCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <MultiSelectFilter testid="filter-region" label="Bölge (çoklu seçim)" className="lg:col-span-2" placeholder="Seçiniz…" disabled={!project_id} emptyText={project_id ? "Bu projede tanımlı bölge yok" : "Önce proje seçin"} values={region_ids} onChange={setRegionIds} options={regionOptions} />
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Hizmet</label>
          <select data-testid="filter-service" disabled={!category_id} value={service_id} onChange={(e) => setService(e.target.value)} className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-md disabled:bg-slate-50 disabled:text-slate-400">
            <option value="">Tümü</option>
            {visibleServices.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="space-y-1 lg:col-span-2">
          <PeriodPicker from={date_from} to={date_to} disabled={!project_id} onChange={(f, t) => { setFrom(f); setTo(t); }} testid="filter-period" />
        </div>
        </div>
      </div>

      {!project_id && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900" data-testid="mandatory-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>Önce bir <strong>Proje</strong> seçin — dönemde iş yapan taşeronlar ve eksik hakedişler listelenir.</div>
        </div>
      )}

      <MissingSubClaims project_id={project_id} category_id={category_id} region_ids={region_ids} date_from={date_from} date_to={date_to} selectedSub={subcontractor_id} reloadKey={missingKey}
        onPick={(r) => { setSub(r.subcontractor_id); if (r.category_id) setCategory(r.category_id); setService(""); }}
        onCreated={() => { reloadClaimInvoices(); setMissingKey((k) => k + 1); }} />

      {data && (
        <>
          <ClaimGroupedTable rows={data.rows} corrections={data.corrections} summaryTotal={data.summary.total_subcontractor} invoices={claimInvoices} returnTo={returnTo} testid="sub-claim-groups"
            totalKey="subcontractor_total" unitKey="subcontractor_unit_price" amountLabel="Taşeron Tutarı" unitLabel="Taşeron Birim" totalLabel="Toplam Ödenecek"
            rowTestid="subcontractor-claim-row" correctionsTestid="sub-claim-corrections-footer" totalTestid="subcontractor-total" />

          <CorrectionsBlock corrections={data.corrections} totalKey="subcontractor_total" showSub testid="sub-claim-corrections" />

          {data.atm && data.atm.rows.length > 0 && (
            <div className="bg-white border-2 border-sky-300 rounded-lg overflow-hidden" data-testid="sub-claim-atm">
              <div className="px-5 py-3 border-b border-sky-200 bg-sky-50 flex items-center justify-between flex-wrap gap-2">
                <div><div className="text-sm font-bold text-slate-900 uppercase tracking-wide">ATM Silimleri</div><div className="text-xs text-slate-500">Gerçekleşen silim × silim fiyatı ödenir; eksik silim bilgi amaçlıdır (ödenmez).</div></div>
                <div className="text-xs text-slate-700">Planlanan <b>{fmtNum(data.atm.planned)}</b> · Gerçekleşen <b className="text-sky-800">{fmtNum(data.atm.actual)}</b> · Eksik <b className={data.atm.missing > 0 ? "text-red-700" : "text-emerald-700"} data-testid="sub-claim-atm-missing">{fmtNum(data.atm.missing)}</b>{data.atm.penalty > 0 && <> · Ceza kesintisi <b className="text-red-700" data-testid="sub-claim-atm-penalty">− {fmtTL(data.atm.penalty)}</b></>}</div>
              </div>
              <table className="w-full tbl">
                <thead className="bg-white"><tr>{["ATM No", "ATM Adı", "İl", "İlçe", "Planlanan", "Gerçekleşen", "Eksik Silim", "Silim Fiyatı", "Tutar"].map((h, i) => <th key={h} className={`px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold ${i >= 4 ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
                <tbody>
                  {data.atm.rows.map((a) => (
                    <tr key={a.location_id} className="border-t border-slate-100" data-testid={`sub-claim-atm-row-${a.location_id}`}>
                      <td className="px-4 py-2 text-xs font-mono font-bold">{a.branch_code}</td>
                      <td className="px-4 py-2 text-xs font-semibold">{a.location_name}</td>
                      <td className="px-4 py-2 text-xs">{a.city}</td>
                      <td className="px-4 py-2 text-xs">{a.district}</td>
                      <td className="numeric-cell px-4 py-2 text-xs">{fmtNum(a.planned)}</td>
                      <td className="numeric-cell px-4 py-2 text-xs font-bold text-sky-800">{fmtNum(a.actual)}</td>
                      <td className={`numeric-cell px-4 py-2 text-xs ${a.missing > 0 ? "text-red-700 font-bold" : "text-emerald-700"}`}>{fmtNum(a.missing)}</td>
                      <td className="numeric-cell px-4 py-2 text-xs">{fmtTL(a.wipe_price)}</td>
                      <td className="numeric-cell px-4 py-2 text-xs font-semibold">{fmtTL(a.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-sky-50 border-t-2 border-sky-200"><tr>
                  <td colSpan={4} className="px-4 py-3 text-xs font-bold text-slate-900 uppercase tracking-wider">ATM Toplamı</td>
                  <td className="numeric-cell px-4 py-3 text-sm font-bold">{fmtNum(data.atm.planned)}</td>
                  <td className="numeric-cell px-4 py-3 text-sm font-bold text-sky-800">{fmtNum(data.atm.actual)}</td>
                  <td className={`numeric-cell px-4 py-3 text-sm font-bold ${data.atm.missing > 0 ? "text-red-700" : "text-emerald-700"}`}>{fmtNum(data.atm.missing)}</td>
                  <td></td>
                  <td className="numeric-cell px-4 py-3 text-sm font-extrabold text-slate-900" data-testid="sub-claim-atm-total">{fmtTL(data.atm.total)}</td>
                </tr>
                {data.atm.penalty > 0 && (
                  <>
                    {(data.atm.penalties || []).map((p) => (
                      <tr key={p.id} className="bg-red-50/60" data-testid={`sub-claim-atm-penalty-row-${p.id}`}>
                        <td className="px-4 py-1.5 text-xs font-mono font-bold text-red-800">{p.branch_code}</td>
                        <td colSpan={7} className="px-4 py-1.5 text-xs text-red-800">Ceza — {p.month}{p.note ? ` · ${p.note}` : ""} <span className="text-slate-500">({p.by})</span></td>
                        <td className="numeric-cell px-4 py-1.5 text-xs font-semibold text-red-700">− {fmtTL(p.amount)}</td>
                      </tr>
                    ))}
                    <tr className="bg-red-50 border-t border-red-200">
                      <td colSpan={8} className="px-4 py-2 text-xs font-bold text-red-800 uppercase tracking-wider">ATM Ceza Kesintisi (taşerona kesilen)</td>
                      <td className="numeric-cell px-4 py-2 text-sm font-bold text-red-700" data-testid="sub-claim-atm-penalty-total">− {fmtTL(data.atm.penalty)}</td>
                    </tr>
                    <tr className="bg-sky-100 border-t-2 border-sky-300">
                      <td colSpan={8} className="px-4 py-3 text-xs font-bold text-slate-900 uppercase tracking-wider">ATM Net (silim tutarı − ceza)</td>
                      <td className="numeric-cell px-4 py-3 text-sm font-extrabold text-slate-900" data-testid="sub-claim-atm-net">{fmtTL(data.atm.net_total)}</td>
                    </tr>
                  </>
                )}</tfoot>
              </table>
            </div>
          )}

          {data.regions && data.regions.length > 0 && (
            <div className="space-y-5" data-testid="subcontractor-service-summary">
              <div>
                <div className="text-sm font-semibold text-slate-900">Hizmet Özeti — Bölge Bazında</div>
                <div className="text-xs text-slate-500">Taşeron fiyatlarıyla; her bölge için hizmet kalemi özeti, en altta genel toplam.</div>
              </div>

              {data.regions.map((rg) => (
                <div key={rg.region_name} className="bg-white border-2 border-slate-300 rounded-lg overflow-hidden" data-testid={`summary-region-${rg.region_name}`}>
                  <div className="px-5 py-3 border-b border-slate-200 bg-slate-100">
                    <div className="text-sm font-bold text-slate-900 uppercase tracking-wide">{rg.region_name}</div>
                  </div>
                  <table className="w-full tbl">
                    <thead className="bg-white">
                      <tr>
                        {ITEM_HEADERS.map((h, i) => (
                          <th key={h} className={`px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold ${itemAlign(i)}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rg.services.map((s, i) => <SummaryRow key={`${s.service_id || s.service_name}-${i}`} s={s} priceKey="subcontractor_unit_price" totalKey="subcontractor_total" />)}
                    </tbody>
                    <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                      <tr>
                        <td colSpan={4} className="px-4 py-3 text-xs font-bold text-slate-900 uppercase tracking-wider">Bölge Toplamı</td>
                        <td className="numeric-cell px-4 py-3 text-sm font-extrabold text-slate-900" data-testid={`summary-region-total-${rg.region_name}`}>{fmtTL(rg.total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ))}

              <div className="bg-white border-2 border-slate-300 rounded-lg overflow-hidden" data-testid="summary-grand-total">
                <div className="px-5 py-3 border-b border-slate-200 bg-slate-100">
                  <div className="text-sm font-bold text-slate-900 uppercase tracking-wide">Genel Toplam</div>
                </div>
                <table className="w-full tbl">
                  <thead className="bg-white">
                    <tr>
                      {ITEM_HEADERS.map((h, i) => (
                        <th key={h} className={`px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold ${itemAlign(i)}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.services.map((s, i) => <SummaryRow key={`${s.service_id || s.service_name}-${i}`} s={s} priceKey="subcontractor_unit_price" totalKey="subcontractor_total" testid={s.is_correction ? "sub-summary-corrections-row" : undefined} />)}
                  </tbody>
                  <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                    <tr>
                      <td colSpan={4} className="px-4 py-3 text-xs font-bold text-slate-900 uppercase tracking-wider">Toplam Ödenecek</td>
                      <td className="numeric-cell px-4 py-3 text-sm font-extrabold text-slate-900" data-testid="subcontractor-summary-total">{fmtTL(data.summary.total_subcontractor)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <ClaimHistory direction="expense" subcontractorId={subcontractor_id || undefined} returnTo={returnTo} testid="sub-claim-history" />
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`mt-1 text-sm ${mono ? "font-mono" : ""} text-slate-900`}>{value}</div>
    </div>
  );
}
